import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { addMonths, format, isAfter, lastDayOfMonth, startOfMonth, sub } from 'date-fns';
import { Badge } from 'react-bootstrap';
import { DateFilter } from './components';
import { getAudiobooksDetails } from './store/actions';
import ArrowBack from './assets/images/icons/arrow-back';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import { filterOptions, filterType } from '../../constants/filterOptions';
import { downloadTableAsExcel, getDateFromString } from '../../utils/helpers';
import TableComponent from '../commons/components/TableComponent';

const AudioBooksDetailsContainer = () => {
  const DEFAULT_RANGE_MONTHS = 6;
  const now = new Date();
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });
  const [startValue, setStartValue] = useState();
  const [endValue, setEndValue] = useState();
  const audiobooksDetails = useSelector((state) => state.kpi.audiobooksDetails);
  const [corporateList, setCorporateList] = useState([]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [currentView, setCurrentView] = useState(filterType.custom);
  const { bookName } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  useEffect(() => {
    if (bookName && state && state.startDate && state.endDate) {
      setStartValue(getDateFromString(state.startDate));
      setEndValue(getDateFromString(state.endDate));
    } else {
      setStartValue(startDateDate);
      setEndValue(endDateDate);
    }
  }, [bookName, state]);

  useEffect(() => {
    if (bookName && startValue && endValue) {
      dispatch(getAudiobooksDetails({
        startDate: format(startValue, 'yyyy-MM-dd'),
        endDate: format(endValue, 'yyyy-MM-dd'),
        bookName,
      }));
    }
  }, [bookName, startValue, endValue]);

  useEffect(() => {
    if (bookName && audiobooksDetails[bookName]) {
      const corporateList = audiobooksDetails[bookName];
      setCorporateList(corporateList);
    }
  }, [bookName, audiobooksDetails]);

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  const monthsInRange = getMonthsInRange(startValue, endValue);

  const columns = useMemo(() => {
    monthsInRange.forEach((month, index) => {
      monthsInRange[index] = getDateFromString(month).toLocaleString('default', {
        month: 'long',
      });
    });
    const monthColumns = monthsInRange.map((monthName) => ({
      Header: monthName,
      accessor: monthName,
      Cell: ({ cell }) => (
        cell.value
      ),
    }));
    const baseColumns = [
      {
        Header: 'Corporate Name',
        accessor: 'corporate_name',
        Cell: ({ cell }) => (
            cell.value
        ),
      },
    ];
    return [...baseColumns, ...monthColumns];
  }, [corporateList, monthsInRange]);

  const data = useMemo(() => {
    if (corporateList.length === 0) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        corporate_name: '-',
        ...monthsInRange.reduce((acc, month) => {
          acc[month] = '-';
          return acc;
        }, {}),
      }));
      return placeholderRows;
    }
    const newArray = [];

    corporateList.map(corporate => {
      const data = {
        corporate_name: corporate.corporate_name,
      }
      corporate.months?.forEach(month => {
        const monthName = getDateFromString(month.active_date).toLocaleString('default', {
          month: 'long',
        });
        data[monthName] = month.unique_users_allocated;
      });
      newArray.push(data);
    });
    return newArray;
  }, [corporateList]);

  console.log(columns, data);

  function downloadSheet() {
    const tableElement = document.getElementById('data-table-container');
    downloadTableAsExcel(tableElement, `Audiobooks ${bookName}.xlsx`);
  }

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilter, sDate, eDate) => {
    setStartValue(sDate);
    setEndValue(eDate);
    setCurrentView(selectedFilter.value);
  };

  return (
    <div className='container-fluid kpi'>
      <div className='company-report'>
        <div className='company-report--actions'>
          <div className='d-flex align-items-center gap-3'>
            <div className='company-report--actions--date'>
              <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
                <Badge className='current-filter' bg='light' text='dark'>
                  {filterOptions.find((option) => option.value === currentView)?.name || 'Custom'}
                </Badge>
                {startValue && endValue && (
                  <span className='date-range'>
                    {format(getDateFromString(startValue), 'MMM d')} -{' '}
                    {format(getDateFromString(endValue), 'MMM d, yyyy')}
                  </span>
                )}
                <span className='select-icon-down' aria-hidden='true'></span>
              </div>
              {showDateFilter && (
                <DateFilter
                  onClose={() => setShowDateFilter(false)}
                  startValue={startValue}
                  endValue={endValue}
                  onFilterUpdate={onFilterUpdate} />
              )}
            </div>
            <div className={`company-report--actions--download`} onClick={() => downloadSheet()}>
              <img src={IconArrowGround} alt='' />
              <span>Download</span>
            </div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-1 ms-2'>
          <ArrowBack onClick={() => navigate(-1)} />
          <h4>Audiobooks: {bookName}</h4>
        </div>
        <div id='data-table-container' className='data-table-container table-responsive mt-3'>
          <TableComponent columns={columns} data={data} />
        </div>
      </div>
    </div>
  );
};

export default AudioBooksDetailsContainer;
